<script setup lang="ts">
import AppGooglePlaceIdForm from '@/views/settings/components/AppGooglePlaceIdForm.vue'
import useStoreUrls from '@/composables/storeUrls'

import type { Store } from '@/views/contests/Components/types/global'

const props = defineProps<{
  type: 'google' | 'facebook' | 'instagram' | 'none' | 'custom' | ''
  isOpen: boolean
  stores: Store[]
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'updated'): void
}>()

const { t } = useI18n()
const { saveMultipleGooglePlaceIds, saveMultipleStoreUrls, isProcessing } = useStoreUrls()

const selectedGooglePlaceIds = ref<Record<number, string>>({})
const selectedFacebookUrls = ref<Record<number, string>>({})
const selectedInstagramUrls = ref<Record<number, string>>({})

const isStoreConfigured = (store: Store) => {
  if (props.type === 'google') {
    return !!selectedGooglePlaceIds.value[store.id] || !!store.googlePlaceId
  }
  if (props.type === 'facebook') {
    return !!selectedFacebookUrls.value[store.id]
  }
  if (props.type === 'instagram') {
    return !!selectedInstagramUrls.value[store.id]
  }
}

const configuredStoresCount = computed(() => {
  return props.stores.filter((store) => isStoreConfigured(store)).length
})

const handleSave = async () => {
  if (props.type === 'google') {
    const storesToSave = props.stores
      .filter((store) => selectedGooglePlaceIds.value[store.id])
      .map((store) => ({
        id: store.id,
        googlePlaceId: selectedGooglePlaceIds.value[store.id],
        currentGooglePlaceId: store.googlePlaceId
      }))

    if (storesToSave.length === 0) {
      return
    }

    const success = await saveMultipleGooglePlaceIds(storesToSave)

    if (success) {
      emit('updated')
    }
  }

  if (props.type === 'facebook' || props.type === 'instagram') {
    let selectedUrls: Record<number, string> = {}
    if (props.type === 'facebook') {
      selectedUrls = selectedFacebookUrls.value
    } else {
      selectedUrls = selectedInstagramUrls.value
    }

    const storesToSave = props.stores
      .filter((store) => {
        return selectedUrls[store.id]
      })
      .map((store) => ({
        id: store.id,
        type: props.type as 'facebook' | 'instagram',
        url: selectedUrls[store.id]
      }))

    if (storesToSave.length === 0) {
      return
    }

    const success = await saveMultipleStoreUrls(storesToSave)

    if (success) {
      emit('updated')
    }
  }
}

const handleClose = () => {
  selectedGooglePlaceIds.value = {}
  selectedFacebookUrls.value = {}
  selectedInstagramUrls.value = {}
  emit('close')
}

const modalTitle = computed(() => {
  if (props.type === 'google') return t('global.storeUrlsModal.google.title')
  if (props.type === 'facebook') return t('global.storeUrlsModal.facebook.title')
  if (props.type === 'instagram') return t('global.storeUrlsModal.instagram.title')
  return ''
})
</script>

<template>
  <BaseModal
    :heading-title="modalTitle"
    :is-open="isOpen"
    :is-processing="isProcessing"
    :cancel="t('global.words.cancel')"
    :link-text="t('global.words.save')"
    size="4xl"
    @click="handleSave"
    @close="handleClose"
  >
    <div class="flex flex-col gap-5">
      <h2 class="text-foreground font-[400] text-sm flex items-center gap-2">
        <BaseIcon name="HeroExclamationTriangleIconOutline" class="h-6 w-6 text-red-600" />
        <span>
          {{
            t('global.storeUrlsModal.remainingStores', {
              configured: configuredStoresCount,
              total: stores.length
            })
          }}
        </span>
      </h2>

      <div class="grid grid-cols-2 gap-5">
        <div
          v-for="store in stores"
          :key="store.id"
          class="overflow-hidden rounded-xl border bg-white duration-300 p-4"
          :data-testid="`marketing-flow-template-${store.id}`"
          clickable
        >
          <div class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <p class="text-foreground font-medium">{{ store.name }}</p>
              <BaseChip
                :text="
                  isStoreConfigured(store)
                    ? t('global.storeUrlsModal.configured')
                    : t('global.storeUrlsModal.notConfigured')
                "
                :color="isStoreConfigured(store) ? 'green' : 'orange'"
              />
            </div>

            <div v-if="type === 'google'" class="flex flex-col gap-2 w-full">
              <p class="text-sm text-foreground font-[400]">
                {{ t('global.storeUrlsModal.google.urlLabel') }}
              </p>
              <AppGooglePlaceIdForm v-model="selectedGooglePlaceIds[store.id]" :google-place-id="store.googlePlaceId" />
            </div>

            <div v-if="type === 'facebook'" class="flex flex-col gap-2 w-full">
              <p class="text-sm text-foreground font-[400]">
                {{ t('global.storeUrlsModal.facebook.urlLabel') }}
              </p>
              <AppFormInput
                id="facebook-url"
                v-model="selectedFacebookUrls[store.id]"
                :placeholder="t('global.storeUrlsModal.facebook.urlPlaceholder')"
              >
                <template #icon-left>
                  <BaseIcon name="IconoirFacebook" class="h-5 w-5 text-blue-600" />
                </template>
              </AppFormInput>
            </div>

            <div v-if="type === 'instagram'" class="flex flex-col gap-2 w-full">
              <p class="text-sm text-foreground font-[400]">
                {{ t('global.storeUrlsModal.instagram.urlLabel') }}
              </p>
              <AppFormInput
                id="instagram-url"
                v-model="selectedInstagramUrls[store.id]"
                :placeholder="t('global.storeUrlsModal.instagram.urlPlaceholder')"
              >
                <template #icon-left>
                  <BaseIcon name="IconoirInstagram" class="h-5 w-5 text-pink-500" />
                </template>
              </AppFormInput>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
